import { useEffect } from "react";
import * as THREE from "three";
import { CSS2DObject } from "three-stdlib";
import { applyMarkerLabelCss2dLayout } from "../coincidentLabelSpread";
import {
  cleanupManagedLabelObject,
  createManagedLabelElement,
  getManagedLabelKind,
  INVENTORY_LABEL_CLASS,
} from "../labelUtils";
import { safeTraverse } from "../scene/sceneObjectLookups";
import { EMPTY_OBJECT } from "../scene/sceneConstants";
import { logger } from "../../utils/logger";

export function useInventoryLabels({
  allMeshes,
  inventoryLabelVisibility,
  safeBodies,
  savedCameras,
  sceneObjects,
  sceneRef,
  structureChangeTick,
}) {
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const cleanupInventoryLabels = () => {
      const existingInventoryLabels = [];
      safeTraverse(scene, (obj) => {
        if (obj?.userData?.isInventoryLabel) existingInventoryLabels.push(obj);
      });
      existingInventoryLabels.forEach((obj) => {
        try {
          cleanupManagedLabelObject(obj);
        } catch (error) {
          logger.warn("[Scene] Failed to clean up inventory label", error);
        }
      });
    };

    cleanupInventoryLabels();

    const anyInventoryLabelsEnabled = Object.values(inventoryLabelVisibility || EMPTY_OBJECT).some(
      Boolean
    );
    if (!anyInventoryLabelsEnabled) return cleanupInventoryLabels;

    const stripLocalPrefix = (name) => {
      if (!name) return name;
      const raw = String(name);
      const slash = raw.lastIndexOf("/");
      return slash >= 0 ? raw.slice(slash + 1) : raw;
    };

    const labelLocalPositionForObject = (anchor) => {
      try {
        anchor.updateWorldMatrix(true, true);
        const box = new THREE.Box3().setFromObject(anchor);
        if (!box.isEmpty()) {
          const top = new THREE.Vector3(
            (box.min.x + box.max.x) / 2,
            box.max.y + 0.04,
            (box.min.z + box.max.z) / 2
          );
          return anchor.worldToLocal(top);
        }
      } catch {
        /* fall through to a stable local offset */
      }
      return new THREE.Vector3(0, 0.08, 0);
    };

    const addInventoryLabel = (anchor, type, id, text) => {
      if (!anchor || id == null || !text) return;
      const labelDiv = createManagedLabelElement({
        className: INVENTORY_LABEL_CLASS,
        text: String(text),
      });
      const label = new CSS2DObject(labelDiv);
      applyMarkerLabelCss2dLayout(label);
      label.position.copy(labelLocalPositionForObject(anchor));
      label.userData = {
        ...(label.userData || {}),
        isInventoryLabel: true,
        inventoryLabelType: type,
        inventoryLabelId: String(id),
        bodyUuid: anchor.userData?.bodyUuid || null,
        stackSpreadSortKey: `inventory:${type}:${String(id)}`,
        stackSpreadBaseLocal: label.position.clone(),
      };
      anchor.add(label);
    };

    const geomLabels = new Map();
    const meshLabels = new Map();
    const inertialLabels = new Map();
    Object.values(safeBodies || EMPTY_OBJECT).forEach((body) => {
      const bodyName = stripLocalPrefix(body?.name) || body?.uuid || "body";
      (body?.geoms || []).forEach((geom) => {
        if (!geom?.uuid) return;
        const geomName = stripLocalPrefix(geom.name) || geom.kind || geom.type || "geom";
        geomLabels.set(`${body.uuid}:${geom.uuid}`, `${geomName} in ${bodyName}`);
        if (geom.mesh && !meshLabels.has(String(geom.mesh))) {
          const mesh = allMeshes?.[geom.mesh];
          meshLabels.set(String(geom.mesh), stripLocalPrefix(mesh?.name) || String(geom.mesh));
        }
      });
      if (body?.inertia?.uuid) {
        inertialLabels.set(
          `${body.uuid}:${body.inertia.uuid}`,
          stripLocalPrefix(body.inertia.name) || bodyName
        );
      }
    });

    Object.values(allMeshes || EMPTY_OBJECT).forEach((mesh) => {
      if (mesh?.uuid && !meshLabels.has(String(mesh.uuid))) {
        meshLabels.set(String(mesh.uuid), stripLocalPrefix(mesh.name) || String(mesh.uuid));
      }
    });

    const seen = {
      mesh: new Set(),
      visual: new Set(),
      collision: new Set(),
      inertial: new Set(),
      camera: new Set(),
    };

    safeTraverse(scene, (obj) => {
      if (!obj?.userData || getManagedLabelKind(obj)) return;
      const ud = obj.userData;
      const bodyUuid = ud.bodyUuid != null ? String(ud.bodyUuid) : "";
      const geomUuid = ud.geomUuid != null ? String(ud.geomUuid) : "";

      if (inventoryLabelVisibility.mesh && ud.meshName != null) {
        const meshKey = String(ud.meshName);
        if (!seen.mesh.has(meshKey)) {
          seen.mesh.add(meshKey);
          addInventoryLabel(obj, "mesh", meshKey, meshLabels.get(meshKey) || meshKey);
        }
      }

      if (
        inventoryLabelVisibility.visual &&
        (ud.type === "mesh" || ud.type === "visual") &&
        bodyUuid &&
        geomUuid
      ) {
        const key = `${bodyUuid}:${geomUuid}`;
        if (!seen.visual.has(key)) {
          seen.visual.add(key);
          addInventoryLabel(obj, "visual", key, geomLabels.get(key) || ud.geomName || geomUuid);
        }
      }

      if (inventoryLabelVisibility.collision && ud.type === "collision" && bodyUuid && geomUuid) {
        const key = `${bodyUuid}:${geomUuid}`;
        if (!seen.collision.has(key)) {
          seen.collision.add(key);
          addInventoryLabel(obj, "collision", key, geomLabels.get(key) || ud.geomName || geomUuid);
        }
      }

      if (inventoryLabelVisibility.inertial && ud.type === "inertial") {
        const inertialUuid = ud.inertialUuid != null ? String(ud.inertialUuid) : "";
        const key = bodyUuid && inertialUuid ? `${bodyUuid}:${inertialUuid}` : inertialUuid;
        if (key && !seen.inertial.has(key)) {
          seen.inertial.add(key);
          addInventoryLabel(
            obj,
            "inertial",
            key,
            inertialLabels.get(key) || stripLocalPrefix(ud.inertialName) || key
          );
        }
      }

      if (inventoryLabelVisibility.camera && ud.type === "camera") {
        const cameraUuid = ud.cameraUuid || ud.savedSceneCameraUuid;
        if (cameraUuid && !seen.camera.has(String(cameraUuid))) {
          const camera = savedCameras?.[cameraUuid];
          seen.camera.add(String(cameraUuid));
          addInventoryLabel(
            obj,
            "camera",
            cameraUuid,
            stripLocalPrefix(camera?.name || ud.cameraName) || String(cameraUuid)
          );
        }
      }
    });

    return cleanupInventoryLabels;
  }, [
    inventoryLabelVisibility,
    sceneObjects,
    structureChangeTick,
    safeBodies,
    allMeshes,
    savedCameras,
    sceneRef,
  ]);
}
